import {
  ICustomFieldRecordSchema,
  IOfferingRes,
  IProductResSchema,
  IQuoteItemReqSchema,
  IQuoteOfferingReqSchema,
  IQuoteOfferingRespSchema,
  IQuoteRespSchema,
  ProductTypeEnum,
} from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import { roundNumberToDecimal } from '@monetize/utils/core';
import { parseISO } from 'date-fns/parseISO';
import { UseFormSetValue } from 'react-hook-form';
import { dispatchCustomEvent } from '../hooks/useCustomEvent';
import { FeatureFlag } from '../services/launchDarkly';
import { logger } from '../services/logger';
import { arrayToObject } from '../utils/misc';

/**
 * This file is dedicated to custom logic that is temporary and specific to sales demos
 * It is all here so that it is easy to identify when it is used and to remove when no longer needed
 */

/**
 * TEMPORARY LOGIC
 * This is for  sales demo for Teleport
 *
 * For one product, we disable the quantity and set it to a value based on the quantity of a different product
 *
 * This is meant to modify the data before sending to the server on PUT /quotes/{quoteId}/offerings
 */
export const TEMP_SALES_DEMO_TELEPORT_calculateQuantityOfDerivedProduct = ({
  teleportSalesDemoTempDependentPricingConfig: featureFlagValue,
  quoteItemsToModify,
  setValue,
}: {
  teleportSalesDemoTempDependentPricingConfig: FeatureFlag['teleportSalesDemoTempDependentPricingConfig'];
  quoteItemsToModify: IQuoteOfferingReqSchema;
  setValue: UseFormSetValue<IQuoteOfferingReqSchema>;
}) => {
  const derivedQuantityConfig = featureFlagValue?.derivedQuantity;
  if (derivedQuantityConfig) {
    const productsById = arrayToObject(quoteItemsToModify.items, 'productId');
    quoteItemsToModify.items.forEach((item, i) => {
      const derivedQuantity = derivedQuantityConfig[item.productId];
      if (derivedQuantity && productsById[derivedQuantity.sourceProduct]) {
        const { quantityMultiplier, sourceProduct } = derivedQuantity;
        const { quantity: sourceProductQuantity } = productsById[sourceProduct];
        const newQuantity = Number(
          (sourceProductQuantity * quantityMultiplier).toFixed(0),
        );
        if (newQuantity !== item.quantity) {
          item.quantity = newQuantity;
          // ensure form updates with changes
          const inputName = `items.${i}.quantity` as const;
          setValue(inputName, newQuantity);
        }
      }
    });
  }
};

export const teleport_ShouldLockQuantity = ({
  customerAutomationTeleport: featureFlagValue,
  productId,
}: {
  customerAutomationTeleport: FeatureFlag['customerAutomationTeleport'];
  productId: string;
}) => {
  try {
    if (!featureFlagValue) {
      return false;
    }

    return (
      productId === featureFlagValue.IncludedTPRs ||
      productId === featureFlagValue.protectedResourcesTRPs
    );
  } catch (ex) {
    logger.error('Error in teleport_QuantityAutomation', ex);
    return false;
  }
};

export const teleport_QuantityAutomation = ({
  customerAutomationTeleport: featureFlagValue,
  quoteItemsToModify,
  setValue,
}: {
  customerAutomationTeleport: FeatureFlag['customerAutomationTeleport'];
  quoteItemsToModify: IQuoteOfferingReqSchema;
  setValue: UseFormSetValue<IQuoteOfferingReqSchema>;
}) => {
  try {
    if (!featureFlagValue) {
      return;
    }
    const includedTPRsIdx = quoteItemsToModify.items.findIndex(
      (item) => item.productId === featureFlagValue.IncludedTPRs,
    );
    const zeroTrustMAUIdx = quoteItemsToModify.items.findIndex(
      (item) => item.productId === featureFlagValue.zeroTrustMAU,
    );
    const protectedResourcesTRPsIdx = quoteItemsToModify.items.findIndex(
      (item) => item.productId === featureFlagValue.protectedResourcesTRPs,
    );

    // Included TPRs = 10 x Zero Trust MAUs
    if (includedTPRsIdx >= 0) {
      const includedTPRs = quoteItemsToModify.items[includedTPRsIdx];
      const zeroTrustMAU = quoteItemsToModify.items[zeroTrustMAUIdx];
      includedTPRs.quantity = (zeroTrustMAU?.quantity || 0) * 10;
      setValue(`items.${includedTPRsIdx}.quantity`, includedTPRs.quantity);
    }

    // Identity Security TPRs = Included Zero Trust TPRs (calculated above) + Additional Zero Trust TPRs
    if (protectedResourcesTRPsIdx >= 0) {
      const additionalZeroTrustTRPsIdx = quoteItemsToModify.items.findIndex(
        (item) => item.productId === featureFlagValue.additionalZeroTrustTRPs,
      );

      const includedTPRs = quoteItemsToModify.items[includedTPRsIdx];
      const additionalZeroTrustTRPs =
        quoteItemsToModify.items[additionalZeroTrustTRPsIdx];
      const protectedResourcesTRPs =
        quoteItemsToModify.items[protectedResourcesTRPsIdx];
      protectedResourcesTRPs.quantity =
        (includedTPRs?.quantity || 0) +
        (additionalZeroTrustTRPs?.quantity || 0);
      setValue(
        `items.${protectedResourcesTRPsIdx}.quantity`,
        protectedResourcesTRPs.quantity,
      );
    }
  } catch (ex) {
    logger.error('Error in teleport_QuantityAutomation', ex);
    return;
  }
};

/**
 * **************************************************************************
 * AcuityMD Automation
 * **************************************************************************
 */
export const CUSTOMER_AUTOMATION_QuoteOffering_ShouldLockQuantity = ({
  customerAutomationAcuityMd: featureFlagValue,
  offeringId,
  productId,
}: {
  customerAutomationAcuityMd: FeatureFlag['customerAutomationAcuityMd'];
  offeringId: string;
  productId: string;
}): boolean => {
  try {
    if (!featureFlagValue?.quantityAutomation?.length) {
      return false;
    }
    return featureFlagValue.quantityAutomation.some(
      ({ scopedOfferingIds, lockTargetQuantities, targetProductIds }) =>
        scopedOfferingIds.includes(offeringId) &&
        lockTargetQuantities &&
        targetProductIds.includes(productId),
    );
  } catch (ex) {
    logger.error(
      'Error in CUSTOMER_AUTOMATION_QuoteOffering_ShouldLockQuantity',
      ex,
    );
    return false;
  }
};

/**
 * Feature-flag based Quantity Automation
 * Quantity of products needs to be updated based on quantity changes of other products
 *
 * This applies to the following scenarios:
 * 1. Quote offering quantity is updated, other products within THE SAME offering need to be updated
 * 2. Quote offering quantity is updated, other products within DIFFERENT offerings need to be updated  (powered by CustomEvent notifications)
 * 3. Quote offering is created, quantity automation is applied from existing quote offerings
 * 4. Optional product added to existing offering, quantity automation is applied from existing quote offerings (same offering or different offering)
 */
export const CUSTOMER_AUTOMATION_QuoteOffering_QuantityAutomation = ({
  customerAutomationAcuityMd: featureFlagValue,
  quoteOffering,
  quoteItemsToModify,
  setValue,
}: {
  customerAutomationAcuityMd: FeatureFlag['customerAutomationAcuityMd'];
  quoteOffering: IQuoteOfferingRespSchema;
  quoteItemsToModify: IQuoteOfferingReqSchema;
  setValue: UseFormSetValue<IQuoteOfferingReqSchema>;
}): (() => void) | undefined => {
  try {
    const quantityAutomation = featureFlagValue?.quantityAutomation;
    if (!quantityAutomation?.length) {
      return;
    }

    const automationToApply = quantityAutomation.find(
      ({ scopedOfferingIds, sourceProductId }) =>
        scopedOfferingIds.includes(quoteOffering.offeringId) &&
        quoteItemsToModify.items.some(
          (item) => item.productId === sourceProductId,
        ),
    );

    if (!automationToApply) {
      return;
    }

    const sourceQuoteItem = quoteItemsToModify.items.find(
      (item) => item.productId === automationToApply.sourceProductId,
    );
    if (!sourceQuoteItem) {
      return;
    }

    const targetProductIds = new Set(automationToApply.targetProductIds);

    quoteItemsToModify.items.forEach((item, i) => {
      if (targetProductIds.has(item.productId)) {
        item.quantity = sourceQuoteItem.quantity;
        // ensure form updates with changes
        setValue(`items.${i}.quantity`, sourceQuoteItem.quantity);
      }
    });

    return () => {
      dispatchCustomEvent('updateQuoteOfferingQty', {
        sourceOffering: {
          quoteOfferingId: quoteOffering.id,
          parentQuoteOfferingId: quoteOffering.parentQuoteOfferingId,
          startDate: parseISO(quoteOffering.startDate),
          endDate: parseISO(quoteOffering.endDate),
        },
        scopedOfferingIds: new Set(automationToApply.scopedOfferingIds),
        targetProductIds: new Set(automationToApply.targetProductIds),
        quantity: sourceQuoteItem.quantity,
      });
    };
  } catch (ex) {
    logger.error(
      'Error in CUSTOMER_AUTOMATION_QuoteOffering_QuantityAutomation',
      ex,
    );
  }
};

/**
 * When an initial quote offering is created, we need to set the initial quantity
 * if there is quantity automation configured that should apply cross-offering
 *
 * @param param0
 * @returns
 */
export const CUSTOMER_AUTOMATION_QuoteOffering_SetInitialQuantity = ({
  customerAutomationAcuityMd: featureFlagValue,
  newQuoteOffering,
  quote,
  setValue,
}: {
  customerAutomationAcuityMd: FeatureFlag['customerAutomationAcuityMd'];
  newQuoteOffering: IQuoteOfferingReqSchema;
  quote: IQuoteRespSchema;
  setValue: UseFormSetValue<IQuoteOfferingReqSchema>;
}) => {
  try {
    if (
      !featureFlagValue?.quantityAutomation?.length ||
      featureFlagValue.quantityAutomation.every(
        ({ scopedOfferingIds }) =>
          !scopedOfferingIds.includes(newQuoteOffering.offeringId),
      )
    ) {
      return;
    }

    const productUpdateMap =
      CUSTOMER_AUTOMATION_QuoteOffering_getInitialQuantityMap({
        customerAutomationAcuityMd: featureFlagValue,
        quote,
      });

    if (!productUpdateMap) {
      return;
    }

    newQuoteOffering.items.forEach((item, i) => {
      try {
        if (productUpdateMap[item.productId]) {
          // We have dependent quantity automation, we need to determine which quantity to use
          const foundItem = getQuantityAutomationForQuoteItem(
            item.productId,
            newQuoteOffering.startDate,
            newQuoteOffering.endDate,
            productUpdateMap,
          );
          if (!foundItem) {
            return;
          }
          item.quantity = foundItem.quantity ?? item.quantity;
          setValue(`items.${i}.quantity`, item.quantity);
        }
      } catch (ex) {
        logger.error(
          'Error setting initial quantity for item in CUSTOMER_AUTOMATION_QuoteOffering_SetInitialQuantity',
          ex,
        );
      }
    });
  } catch (ex) {
    logger.error(
      'Error in CUSTOMER_AUTOMATION_QuoteOffering_SetInitialQuantity',
      ex,
    );
  }
};

/**
 * Get all the quantity automation values for each product in the quote
 */
export const CUSTOMER_AUTOMATION_QuoteOffering_getInitialQuantityMap = ({
  customerAutomationAcuityMd: featureFlagValue,
  quote,
}: {
  customerAutomationAcuityMd: FeatureFlag['customerAutomationAcuityMd'];
  quote: IQuoteRespSchema;
}) => {
  try {
    const quantityAutomation = featureFlagValue?.quantityAutomation;
    if (!quantityAutomation?.length) {
      return;
    }

    const quantityAutomationByOfferingId = quantityAutomation.reduce(
      (acc, { scopedOfferingIds, sourceProductId, targetProductIds }) => {
        scopedOfferingIds.forEach((offeringId) => {
          if (!acc[offeringId]) {
            acc[offeringId] = { sourceProductId, targetProductIds };
          }
        });
        return acc;
      },
      {} as Record<
        string,
        { sourceProductId: string; targetProductIds: string[] }
      >,
    );

    const productUpdateMap: Record<
      string,
      { startDate: string; endDate: string; quantity: number }[]
    > = {};

    quote.quoteOfferings.forEach((quoteOffering) => {
      const currentAutomation =
        quantityAutomationByOfferingId[quoteOffering.offeringId];
      if (currentAutomation) {
        quoteOffering.items.forEach((quoteItem) => {
          // Check if product has a target product that needs to be updated
          if (currentAutomation.sourceProductId === quoteItem.productId) {
            currentAutomation.targetProductIds.forEach((targetProductId) => {
              productUpdateMap[targetProductId] =
                productUpdateMap[targetProductId] || [];
              productUpdateMap[targetProductId].push({
                startDate: quoteOffering.startDate,
                endDate: quoteOffering.endDate,
                quantity: quoteItem.quantity,
              });
            });
          }
        });
      }
    });

    return productUpdateMap;
  } catch (ex) {
    logger.error(
      'Error in CUSTOMER_AUTOMATION_QuoteOffering_getInitialQuantityMap',
      ex,
    );
    return;
  }
};

/**
 * For a given product id, see if there is a quantity automation that should be applied
 *
 * @param productId
 * @param startDate
 * @param endDate
 * @param productUpdateMap
 * @returns
 */
export const getQuantityAutomationForQuoteItem = (
  productId: string,
  startDate: Maybe<string>,
  endDate: Maybe<string>,
  productUpdateMap: Record<
    string,
    { startDate: string; endDate: string; quantity: number }[]
  >,
) => {
  if (!startDate || !endDate) {
    // if we don't have start/end dates then we cannot figure out the correct bounding, so just use the last one
    return productUpdateMap[productId]?.reverse()[0];
  }
  return productUpdateMap[productId]?.reverse()?.find((item) => {
    // if we don't have an end date, then we just guess that the start date is the only thing we care about
    // list is reversed so we default to the last item as it is probably the best guess
    if (startDate && !endDate) {
      return startDate <= item.endDate;
    }
    return startDate <= item.endDate && endDate >= item.startDate;
  });
};

export const CUSTOMER_AUTOMATION_liveEo_shouldLockCustomPrice = ({
  productId,
  customerAutomationLiveEo: featureFlagValue,
}: {
  productId: string;
  customerAutomationLiveEo: FeatureFlag['customerAutomationLiveEo'];
}): boolean => {
  if (!featureFlagValue) {
    return false;
  }
  return productId === featureFlagValue.totalScansProductId;
};

export const CUSTOMER_AUTOMATION_liveEo = ({
  quoteOffering,
  quoteItemsToModify,
  customerAutomationLiveEo: featureFlagValue,
  setValue,
}: {
  quoteOffering?: Maybe<IQuoteOfferingRespSchema>;
  quoteItemsToModify: IQuoteOfferingReqSchema;
  customerAutomationLiveEo: FeatureFlag['customerAutomationLiveEo'];
  setValue: UseFormSetValue<IQuoteOfferingReqSchema>;
}) => {
  if (!featureFlagValue || !quoteOffering) {
    return;
  }
  const scanCost = quoteOffering.items.find(
    (item) => item.productId === featureFlagValue.scanCostProductId,
  );
  const totalScansIdx = quoteItemsToModify.items.findIndex(
    (item) => item.productId === featureFlagValue.totalScansProductId,
  );
  const totalScans = quoteItemsToModify.items[totalScansIdx];

  if (!scanCost || totalScansIdx < 0) {
    return;
  }

  const totalScansQuantity = totalScans.quantity || 0;
  const scanCostAmount = scanCost.amount || 0;
  const totalScanAmount = scanCostAmount * Math.max(0, totalScansQuantity - 1);
  const totalScanPpu = totalScanAmount / Math.max(1, totalScansQuantity);
  if (isNaN(totalScanPpu) || !isFinite(totalScanPpu)) {
    return;
  }

  quoteItemsToModify.items[totalScansIdx].customPrice = {
    ...quoteItemsToModify.items[totalScansIdx].customPrice,
    unitAmount: totalScanPpu,
  };
  setValue(`items.${totalScansIdx}.customPrice.unitAmount`, totalScanPpu);
};

export const CUSTOMER_AUTOMATION_liveEo_requireSecondUpdate = ({
  priorQuoteOffering,
  quoteOfferingResponse,
  customerAutomationLiveEo: featureFlagValue,
}: {
  priorQuoteOffering: Maybe<IQuoteOfferingRespSchema>;
  quoteOfferingResponse: IQuoteOfferingRespSchema;
  customerAutomationLiveEo: FeatureFlag['customerAutomationLiveEo'];
}) => {
  if (
    !featureFlagValue?.scanCostProductId ||
    !priorQuoteOffering ||
    !quoteOfferingResponse
  ) {
    return;
  }
  const scanCostPrior = priorQuoteOffering.items.find(
    (item) => item.productId === featureFlagValue.scanCostProductId,
  );
  const scanCostCurrent = quoteOfferingResponse.items.find(
    (item) => item.productId === featureFlagValue.scanCostProductId,
  );

  return scanCostPrior?.amount !== scanCostCurrent?.amount;
};

export const CUSTOMER_AUTOMATION_LangChain = ({
  quoteItemsToModify,
  setValue,
  customerAutomationLangChain: featureFlagValue,
}: {
  quoteItemsToModify: IQuoteOfferingReqSchema;
  setValue: UseFormSetValue<IQuoteOfferingReqSchema>;
  customerAutomationLangChain: FeatureFlag['customerAutomationLangChain'];
}) => {
  if (!featureFlagValue) {
    return;
  }

  const {
    creditProductId,
    premiumSupportProductId,
    premiumSupportWithServicesProductId,
    standardSupportProductId,
  } = featureFlagValue;

  function getSupportPricePerUnit(
    credits: number,
    basePrice: number,
    initialDiscount = 0.015,
    discountRateOfChange = -0.001,
  ) {
    const STEP_SIZE = 10_000;
    const FIRST_DISCOUNTED_CREDITS = 110_000;

    // Everything below 110k uses the base price
    if (credits < FIRST_DISCOUNTED_CREDITS) {
      return basePrice;
    }

    let price = basePrice;
    let discount = initialDiscount;

    // Apply discount for each 10k step from 110k up to credits
    for (
      let step = FIRST_DISCOUNTED_CREDITS;
      step <= credits;
      step += STEP_SIZE
    ) {
      price *= 1 - discount;
      discount *= 1 + discountRateOfChange; // discount decreases each step
    }

    return roundNumberToDecimal(price, 5);
  }

  function getProductInfo() {
    let idx = quoteItemsToModify.items.findIndex(
      (item) => item.productId === premiumSupportWithServicesProductId,
    );
    if (idx >= 0) {
      return {
        productIdx: idx,
        product: quoteItemsToModify.items[idx],
        basePpu: 0.0075,
        initialDiscount: 0.015,
        discountRateOfChange: -0.001,
      };
    }

    idx = quoteItemsToModify.items.findIndex(
      (item) => item.productId === premiumSupportProductId,
    );
    if (idx >= 0) {
      return {
        productIdx: idx,
        product: quoteItemsToModify.items[idx],
        basePpu: 0.00675,
        initialDiscount: 0.015,
        discountRateOfChange: -0.001,
      };
    }

    idx = quoteItemsToModify.items.findIndex(
      (item) => item.productId === standardSupportProductId,
    );
    if (idx >= 0) {
      return {
        productIdx: idx,
        product: quoteItemsToModify.items[idx],
        basePpu: 0.005,
        initialDiscount: 0.0125,
        discountRateOfChange: 0,
      };
    }
    return {
      productIdx: null,
      product: null,
      basePpu: null,
      initialDiscount: 0.0125,
      discountRateOfChange: 0,
    };
  }

  const creditsProduct = quoteItemsToModify.items.find(
    (item) => item.productId === creditProductId,
  );

  const {
    productIdx,
    product,
    basePpu,
    initialDiscount,
    discountRateOfChange,
  } = getProductInfo();

  if (!creditsProduct || !product) {
    return;
  }

  const ppu = getSupportPricePerUnit(
    creditsProduct.quantity,
    basePpu,
    initialDiscount,
    discountRateOfChange,
  );

  quoteItemsToModify.items[productIdx].customPrice = {
    ...quoteItemsToModify.items[productIdx].customPrice,
    unitAmount: ppu,
  };
  setValue(`items.${productIdx}.customPrice.unitAmount`, ppu);
};

export const CUSTOMER_AUTOMATION_LangChain_shouldLockCustomPrice = ({
  productId,
  customerAutomationLangChain: featureFlagValue,
}: {
  productId: string;
  customerAutomationLangChain: FeatureFlag['customerAutomationLangChain'];
}): boolean => {
  if (!featureFlagValue) {
    return false;
  }
  return (
    productId === featureFlagValue.premiumSupportProductId ||
    productId === featureFlagValue.premiumSupportWithServicesProductId ||
    productId === featureFlagValue.standardSupportProductId
  );
};

export const CUSTOMER_AUTOMATION_LangChain_requireSecondUpdate = ({
  priorQuoteOffering,
  quoteOfferingResponse,
  customerAutomationLangChain: featureFlagValue,
}: {
  priorQuoteOffering: Maybe<IQuoteOfferingRespSchema>;
  quoteOfferingResponse: IQuoteOfferingRespSchema;
  customerAutomationLangChain: FeatureFlag['customerAutomationLangChain'];
}) => {
  if (
    !featureFlagValue?.creditProductId ||
    !priorQuoteOffering ||
    !quoteOfferingResponse
  ) {
    return;
  }

  const { creditProductId } = featureFlagValue;

  const priorAmount = priorQuoteOffering.items.find(
    (item) => item.productId === creditProductId,
  )?.amount;
  const currentAmount = quoteOfferingResponse.items.find(
    (item) => item.productId === creditProductId,
  )?.amount;

  return priorAmount !== currentAmount;
};

export const CUSTOMER_AUTOMATION_Coveo_shouldLockCustomPrice = ({
  productId,
  customerAutomationCoveo: featureFlagValue,
}: {
  productId: string;
  customerAutomationCoveo: FeatureFlag['customerAutomationCoveo'];
}): boolean => {
  if (!featureFlagValue) {
    return false;
  }
  return productId === featureFlagValue.customFormulaProductId;
};

export const CUSTOMER_AUTOMATION_Coveo = ({
  quoteOffering,
  quoteItemsToModify,
  customerAutomationCoveo: featureFlagValue,
  setValue,
}: {
  quoteOffering?: Maybe<IQuoteOfferingRespSchema>;
  quoteItemsToModify: IQuoteOfferingReqSchema;
  customerAutomationCoveo: FeatureFlag['customerAutomationCoveo'];
  setValue: UseFormSetValue<IQuoteOfferingReqSchema>;
}) => {
  if (!featureFlagValue || !quoteOffering) {
    return;
  }

  const quoteItemIdx = quoteItemsToModify.items.findIndex(
    (item) => item.productId === featureFlagValue.customFormulaProductId,
  );

  if (quoteItemIdx < 0) {
    return;
  }

  const quoteItem = quoteItemsToModify.items[quoteItemIdx];

  const input = {
    monthlyUnitListPrice: 5_000,
    minQty: 1,
    maxQty: 100,
    p4: 1,
    p5: 10,
    p6: 2,
    p7: 250,
    p8: 'PERCENT',
    p9: 0.2,
    p10: 1,
    p11: 0.95,
    p12: 10_000.0,
    p13: 20_000_000.0,
    p14: 0.55,
  };
  const calc1 = input.maxQty - input.minQty;
  const calc2 = input.minQty / (input.maxQty - input.minQty);
  const calc3 = 9.9 * 10 ** -input.p4;
  const calc4 = Math.log(calc3);
  const calc5 = input.monthlyUnitListPrice / input.p5;
  const calc6 = input.monthlyUnitListPrice / input.p5;

  const monthlyValp =
    input.minQty * input.monthlyUnitListPrice -
    (((calc3 ** (Math.min(quoteItem.quantity, input.maxQty) / calc1) -
      calc3 ** calc2) *
      calc1 -
      (Math.min(quoteItem.quantity, input.maxQty) - input.minQty) *
        calc3 ** (input.maxQty / calc1) *
        calc4) *
      input.monthlyUnitListPrice -
      (calc3 ** (Math.min(quoteItem.quantity, input.maxQty) / calc1) -
        calc3 ** calc2) *
        (calc5 - 0.0000001) *
        input.maxQty +
      (calc3 ** (Math.min(quoteItem.quantity, input.maxQty) / calc1) -
        calc3 ** calc2) *
        (calc5 - 0.0000001) *
        input.minQty +
      (Math.min(quoteItem.quantity, input.maxQty) - input.minQty) *
        calc3 ** calc2 *
        calc4 *
        (calc5 - 0.0000001)) /
      ((calc3 - 1) * calc3 ** calc2 * calc4) +
    Math.max(0, quoteItem.quantity - input.maxQty) * calc6;

  const monthlyUnitPriceAtValp = monthlyValp / quoteItem.quantity;

  const ppu =
    monthlyUnitPriceAtValp *
    (1 + Math.min(input.p9 * input.p10, 1)) *
    (quoteOffering.billingFrequencyInMonths || 1);

  if (isNaN(ppu) || !isFinite(ppu)) {
    return;
  }

  quoteItemsToModify.items[quoteItemIdx].customPrice = {
    ...quoteItemsToModify.items[quoteItemIdx].customPrice,
    unitAmount: ppu,
  };
  setValue(`items.${quoteItemIdx}.customPrice.unitAmount`, ppu);
};

/**
 * **************************************************************************
 * Fexa Automation
 *
 * The CMMS product quantity ("# of Locations") drives the onboarding target(s):
 * their custom unit price =
 *   unitMultiplier * (coefficient * cmmsQuantity + intercept)
 * (quantity stays 1, quantity field is locked). The source and target products
 * may be in the same offering or in different offerings.
 * **************************************************************************
 */

/**
 * Derive the onboarding custom unit price from the CMMS quantity.
 * Rounded to 2 decimal places (a dollar unit price).
 */
export const fexa_calculateOnboardingUnitPrice = (
  cmmsQuantity: number,
  {
    unitMultiplier,
    coefficient,
    intercept,
  }: { unitMultiplier: number; coefficient: number; intercept: number },
): number => {
  const price = unitMultiplier * (coefficient * cmmsQuantity + intercept);
  return roundNumberToDecimal(price, 2);
};

/**
 * Find the CMMS (source) quantity anywhere on the quote. Used to derive the
 * initial target values when the source lives in a different offering.
 */
const fexa_findCmmsQuantityOnQuote = (
  cmmsProductId: string,
  quote?: Maybe<IQuoteRespSchema>,
): number | undefined => {
  for (const quoteOffering of quote?.quoteOfferings ?? []) {
    const cmmsItem = quoteOffering.items.find(
      (item) => item.productId === cmmsProductId,
    );
    if (cmmsItem) {
      return cmmsItem.quantity;
    }
  }
  return undefined;
};

/**
 * Should the quantity field be locked for this product? Onboarding targets
 * have their quantities locked (they stay at 1).
 */
export const CUSTOMER_AUTOMATION_Fexa_shouldLockQuantity = ({
  customerAutomationFexa: featureFlagValue,
  productId,
}: {
  customerAutomationFexa: FeatureFlag['customerAutomationFexa'];
  productId: string;
}): boolean => {
  if (!featureFlagValue) {
    return false;
  }
  return (featureFlagValue.onboarding?.targetProductIds ?? []).includes(
    productId,
  );
};

/**
 * Should the custom price field be locked for this product? Only the
 * onboarding target price is derived (and therefore locked).
 */
export const CUSTOMER_AUTOMATION_Fexa_shouldLockCustomPrice = ({
  customerAutomationFexa: featureFlagValue,
  productId,
}: {
  customerAutomationFexa: FeatureFlag['customerAutomationFexa'];
  productId: string;
}): boolean => {
  if (!featureFlagValue) {
    return false;
  }
  return (featureFlagValue.onboarding?.targetProductIds ?? []).includes(
    productId,
  );
};

/**
 * Main Fexa automation, applied when saving/creating a quote offering.
 *
 * Sets the onboarding custom unit price for any onboarding target products
 * present in the offering being saved. The driving CMMS quantity is taken from
 * this offering when present, and otherwise (only when adding a new offering)
 * from wherever the source lives on the quote.
 *
 * When the CMMS source lives in THIS offering, a callback is returned that,
 * given the saved offering, notifies other offerings (which may hold the
 * targets) to recalculate. It is returned on add as well as update, and
 * resolves once every target offering has finished saving.
 */
export const CUSTOMER_AUTOMATION_Fexa = ({
  customerAutomationFexa: featureFlagValue,
  quoteOffering,
  quote,
  quoteItemsToModify,
  setValue,
  suppressBroadcast = false,
}: {
  customerAutomationFexa: FeatureFlag['customerAutomationFexa'];
  quoteOffering?: Maybe<IQuoteOfferingRespSchema>;
  quote?: Maybe<IQuoteRespSchema>;
  quoteItemsToModify: IQuoteOfferingReqSchema;
  setValue: UseFormSetValue<IQuoteOfferingReqSchema>;
  /**
   * Set for a save triggered by another offering's event. It must not
   * broadcast back: listeners compare against pre-save snapshots, so two
   * offerings that each hold a CMMS source and an onboarding target would keep
   * re-saving each other while the first save waits on them.
   */
  suppressBroadcast?: boolean;
}):
  | ((savedQuoteOffering: IQuoteOfferingRespSchema) => Promise<unknown>)
  | undefined => {
  try {
    if (!featureFlagValue) {
      return;
    }
    const { cmmsProductId, onboarding } = featureFlagValue;

    // Prefer the CMMS source in the offering being saved (freshest value).
    const cmmsItemInCurrentOffering = quoteItemsToModify.items.find(
      (item) => item.productId === cmmsProductId,
    );

    let cmmsQuantity: number | undefined;
    if (cmmsItemInCurrentOffering) {
      cmmsQuantity = cmmsItemInCurrentOffering.quantity;
    } else if (!quoteOffering?.id) {
      // Adding a new offering whose CMMS source lives elsewhere — derive the
      // initial target values from the quote. We intentionally do NOT re-derive
      // from the quote when UPDATING a target-only offering: those values are
      // already set at creation and kept in sync via the cross-offering event,
      // and a stale quote read here could clobber a fresher event update.
      cmmsQuantity = fexa_findCmmsQuantityOnQuote(cmmsProductId, quote);
    }

    if (cmmsQuantity == null) {
      // CMMS source not available for this offering — nothing to derive
      return;
    }

    const onboardingTargetIds = new Set(onboarding?.targetProductIds ?? []);

    // Onboarding custom unit price (quantity forced to 1)
    if (onboarding && onboardingTargetIds.size) {
      const unitAmount = fexa_calculateOnboardingUnitPrice(
        cmmsQuantity,
        onboarding,
      );
      quoteItemsToModify.items.forEach((item, i) => {
        // No custom price id check: a line in a newly added offering has none
        // until it is saved. The request drops customPrice for any line whose
        // rate price is not a custom price model.
        if (!onboardingTargetIds.has(item.productId)) {
          return;
        }
        item.customPrice = { ...item.customPrice, unitAmount };
        setValue(`items.${i}.customPrice.unitAmount`, unitAmount);
        if (item.quantity !== 1) {
          item.quantity = 1;
          setValue(`items.${i}.quantity`, 1);
        }
      });
    }

    // Cross-offering: if the CMMS source is in this offering, notify offerings
    // that may hold the targets so they can recalculate.
    if (cmmsItemInCurrentOffering && !suppressBroadcast) {
      return (savedQuoteOffering) => {
        const pendingSaves: Promise<unknown>[] = [];
        dispatchCustomEvent('updateQuoteOfferingFexaCmms', {
          sourceOffering: {
            quoteOfferingId: savedQuoteOffering.id,
            parentQuoteOfferingId: savedQuoteOffering.parentQuoteOfferingId,
            startDate: parseISO(savedQuoteOffering.startDate),
            endDate: parseISO(savedQuoteOffering.endDate),
          },
          cmmsQuantity,
          waitUntil: (save) => {
            pendingSaves.push(save);
          },
        });
        // allSettled: a failed target save must not abort the source's own
        // post-save steps
        return Promise.allSettled(pendingSaves);
      };
    }
  } catch (ex) {
    logger.error('Error in CUSTOMER_AUTOMATION_Fexa', ex);
  }
};

/**
 * **************************************************************************
 * Red Oak Automation
 *
 * The "Team Communications" product's quantity is locked and derived as the
 * sum of the other (source) products' quantities within the same offering.
 * **************************************************************************
 */

export const CUSTOMER_AUTOMATION_RedOak_shouldLockQuantity = ({
  customerAutomationRedOak: featureFlagValue,
  offeringId,
  productId,
}: {
  customerAutomationRedOak: FeatureFlag['customerAutomationRedOak'];
  offeringId: string;
  productId: string;
}): boolean => {
  try {
    if (!featureFlagValue) {
      return false;
    }
    return (
      (featureFlagValue.scopedOfferingIds ?? []).includes(offeringId) &&
      productId === featureFlagValue.mainProductId
    );
  } catch (ex) {
    logger.error('Error in CUSTOMER_AUTOMATION_RedOak_shouldLockQuantity', ex);
    return false;
  }
};

export const CUSTOMER_AUTOMATION_RedOak_QuantityAutomation = ({
  customerAutomationRedOak: featureFlagValue,
  quoteItemsToModify,
  setValue,
}: {
  customerAutomationRedOak: FeatureFlag['customerAutomationRedOak'];
  quoteItemsToModify: IQuoteOfferingReqSchema;
  setValue: UseFormSetValue<IQuoteOfferingReqSchema>;
}) => {
  try {
    // Use quoteItemsToModify.offeringId (always present, add or update) rather
    // than a quoteOffering response, which is undefined until an offering has
    // been created — the main product's quantity must be derived on create too,
    // since its quantity field is already locked from the very first render.
    if (
      !featureFlagValue ||
      !(featureFlagValue.scopedOfferingIds ?? []).includes(
        quoteItemsToModify.offeringId,
      )
    ) {
      return;
    }

    const { mainProductId, sourceProductIds } = featureFlagValue;
    const sourceProductIdSet = new Set(sourceProductIds);
    // Guard against misconfiguration: if mainProductId is accidentally also
    // listed as a source, excluding it here prevents the derived quantity
    // from summing itself into unbounded growth on every subsequent save.
    sourceProductIdSet.delete(mainProductId);

    const mainItemIdx = quoteItemsToModify.items.findIndex(
      (item) => item.productId === mainProductId,
    );
    if (mainItemIdx < 0) {
      return;
    }

    const summedQuantity = quoteItemsToModify.items.reduce(
      (total, item) =>
        sourceProductIdSet.has(item.productId)
          ? total + (item.quantity || 0)
          : total,
      0,
    );

    if (quoteItemsToModify.items[mainItemIdx].quantity !== summedQuantity) {
      quoteItemsToModify.items[mainItemIdx].quantity = summedQuantity;
      setValue(`items.${mainItemIdx}.quantity`, summedQuantity);
    }
  } catch (ex) {
    logger.error('Error in CUSTOMER_AUTOMATION_RedOak_QuantityAutomation', ex);
  }
};

/**
 * **************************************************************************
 * Custom-field quantity automation (roll-up + 1:1 mirror)
 *
 * Unlike the automations above, products opt in through PRODUCT custom fields
 * rather than hard-coded ids. The custom field API names come from the
 * `customerAutomationCustomFieldQuantity` feature flag.
 *
 * Roll-up (within one offering):
 * - Roll-up TARGET (checkbox): quantity and unit price are locked. Quantity is
 *   derived as the sum of every roll-up SOURCE line in the same offering whose
 *   product type is Subscription (`ProductTypeEnum.ADVANCE`).
 * - Roll-up SOURCE (checkbox): freely editable; feeds the target sum.
 *
 * Mirror (anywhere on the quote):
 * - Quantity-locked (checkbox): quantity is locked and mirrors, 1:1, the
 *   quantity of the non-locked line on the quote sharing its mirror match key
 *   (dropdown). Lines in the offering being saved are preferred; other
 *   offerings are consulted when a line is first added and kept in sync
 *   afterwards through the `updateQuoteOfferingMirrorQty` custom event.
 *
 * Derived quantities never drop below MIN_AUTOMATED_QUANTITY. Scheduled
 * changes are separate quote offerings per period, so quote lookups and the
 * event only pair offerings whose dates overlap.
 * **************************************************************************
 */

type CustomFieldQuantityConfig =
  FeatureFlag['customerAutomationCustomFieldQuantity'];

/** The subset of a catalog product the automation needs */
export type QuantityAutomationCatalogProduct = Pick<
  IProductResSchema,
  'id' | 'productType' | 'customFields'
>;

export type QuantityAutomationProductsById = Record<
  string,
  QuantityAutomationCatalogProduct
>;

/**
 * Catalog products keyed by id across the given offerings. The quote page's
 * orderables list is decorated with dropdown group-header rows that have no
 * products (typed away as offerings), so rows without a product array are
 * skipped. A product listed under several offerings keeps one entry.
 */
export const getQuantityAutomationProductsById = (
  offerings: Partial<Pick<IOfferingRes, 'products'>>[],
): QuantityAutomationProductsById =>
  arrayToObject(
    offerings.flatMap(({ products }) => products ?? []),
    'id',
  );

export interface ProductQuantityAutomationFlags {
  isRollUpSource: boolean;
  isRollUpTarget: boolean;
  isQuantityLocked: boolean;
  /** Pairs a quantity-locked line with the line it mirrors */
  mirrorMatchKey: string | null;
}

/**
 * The catalog enforces a minimum quantity of 1, so a derived quantity (an empty
 * roll-up, a zero source) is floored rather than saved as 0.
 */
const MIN_AUTOMATED_QUANTITY = 1;

const NO_QUANTITY_AUTOMATION_FLAGS: ProductQuantityAutomationFlags = {
  isRollUpSource: false,
  isRollUpTarget: false,
  isQuantityLocked: false,
  mirrorMatchKey: null,
};

const isCustomFieldChecked = (
  customFields: NonNullable<IProductResSchema['customFields']>,
  key: string,
): boolean => {
  const value = customFields[key];
  return value === true || value === 'true';
};

const getCustomFieldText = (
  customFields: NonNullable<IProductResSchema['customFields']>,
  key: string,
): string | null => {
  const value = customFields[key];
  return typeof value === 'string' && value.trim() ? value : null;
};

/**
 * Read the automation flags off a catalog product's custom fields.
 * Everything is false/null when the flag is off or the product is unknown.
 */
export const getProductQuantityAutomationFlags = (
  config: CustomFieldQuantityConfig,
  product: Maybe<Pick<QuantityAutomationCatalogProduct, 'customFields'>>,
): ProductQuantityAutomationFlags => {
  if (!config || !product?.customFields) {
    return NO_QUANTITY_AUTOMATION_FLAGS;
  }
  const { customFields } = product;
  return {
    isRollUpSource: isCustomFieldChecked(customFields, config.rollUpSourceKey),
    isRollUpTarget: isCustomFieldChecked(customFields, config.rollUpTargetKey),
    isQuantityLocked: isCustomFieldChecked(
      customFields,
      config.quantityLockedKey,
    ),
    mirrorMatchKey: getCustomFieldText(customFields, config.mirrorMatchKey),
  };
};

/** Roll-up targets and quantity-locked lines cannot have their quantity edited */
export const CUSTOMER_AUTOMATION_CustomField_shouldLockQuantity = ({
  customerAutomationCustomFieldQuantity: config,
  product,
}: {
  customerAutomationCustomFieldQuantity: CustomFieldQuantityConfig;
  product: Maybe<Pick<QuantityAutomationCatalogProduct, 'customFields'>>;
}): boolean => {
  const { isRollUpTarget, isQuantityLocked } =
    getProductQuantityAutomationFlags(config, product);
  return isRollUpTarget || isQuantityLocked;
};

/** Roll-up targets keep their catalog ($0 custom) price; reps cannot edit it */
export const CUSTOMER_AUTOMATION_CustomField_shouldLockCustomPrice = ({
  customerAutomationCustomFieldQuantity: config,
  product,
}: {
  customerAutomationCustomFieldQuantity: CustomFieldQuantityConfig;
  product: Maybe<Pick<QuantityAutomationCatalogProduct, 'customFields'>>;
}): boolean =>
  getProductQuantityAutomationFlags(config, product).isRollUpTarget;

/**
 * Derive every roll-up target quantity in the offering being saved as the sum
 * of the offering's Subscription roll-up source quantities. Runs on add and
 * update; sources and targets never cross offerings.
 */
export const CUSTOMER_AUTOMATION_CustomField_RollUpQuantity = ({
  customerAutomationCustomFieldQuantity: config,
  productsById,
  quoteItemsToModify,
  setValue,
}: {
  customerAutomationCustomFieldQuantity: CustomFieldQuantityConfig;
  productsById: QuantityAutomationProductsById;
  quoteItemsToModify: IQuoteOfferingReqSchema;
  /** Omit when the data is not backed by a form (scheduled change creation) */
  setValue?: UseFormSetValue<IQuoteOfferingReqSchema>;
}) => {
  try {
    if (!config) {
      return;
    }
    const { items } = quoteItemsToModify;
    const flagsByIndex = items.map(({ productId }) =>
      getProductQuantityAutomationFlags(config, productsById[productId]),
    );
    const targetIndexes = flagsByIndex.flatMap(({ isRollUpTarget }, index) =>
      isRollUpTarget ? [index] : [],
    );
    if (!targetIndexes.length) {
      return;
    }

    const sourceTotal = items.reduce(
      (total, { productId, quantity }, index) => {
        const { isRollUpSource, isRollUpTarget } = flagsByIndex[index];
        // Guard against misconfiguration: a target that is also flagged as a
        // source would otherwise sum itself into unbounded growth on every save
        if (
          !isRollUpSource ||
          isRollUpTarget ||
          productsById[productId]?.productType !== ProductTypeEnum.ADVANCE
        ) {
          return total;
        }
        return total + (quantity || 0);
      },
      0,
    );

    const targetQuantity = Math.max(MIN_AUTOMATED_QUANTITY, sourceTotal);

    targetIndexes.forEach((index) => {
      if (items[index].quantity !== targetQuantity) {
        items[index].quantity = targetQuantity;
        setValue?.(`items.${index}.quantity`, targetQuantity);
      }
    });
  } catch (ex) {
    logger.error('Error in CUSTOMER_AUTOMATION_CustomField_RollUpQuantity', ex);
  }
};

/**
 * Quantity of each mirror SOURCE (a non-locked line with a match key) among the
 * given lines, keyed by match key. The first line found per key wins.
 *
 * Roll-up targets are excluded: their quantity is derived after mirroring, so
 * they would broadcast a stale pre-roll-up value.
 */
const getMirrorSourceQuantities = (
  config: NonNullable<CustomFieldQuantityConfig>,
  productsById: QuantityAutomationProductsById,
  items: Pick<IQuoteItemReqSchema, 'productId' | 'quantity'>[],
): Record<string, number> => {
  const quantityByMatchKey: Record<string, number> = Object.create(null);
  items.forEach(({ productId, quantity }) => {
    const { isQuantityLocked, isRollUpTarget, mirrorMatchKey } =
      getProductQuantityAutomationFlags(config, productsById[productId]);
    if (
      isQuantityLocked ||
      isRollUpTarget ||
      !mirrorMatchKey ||
      Object.hasOwn(quantityByMatchKey, mirrorMatchKey)
    ) {
      return;
    }
    quantityByMatchKey[mirrorMatchKey] = Math.max(
      MIN_AUTOMATED_QUANTITY,
      quantity || 0,
    );
  });
  return quantityByMatchKey;
};

/**
 * Does the period [startDate, endDate] overlap a quote offering's dates?
 * ISO date strings compare lexicographically. Without a start date nothing is
 * known, so everything overlaps; without an end date the period is open-ended.
 */
const doesPeriodOverlapOffering = (
  {
    startDate: offeringStartDate,
    endDate: offeringEndDate,
  }: Pick<IQuoteOfferingRespSchema, 'startDate' | 'endDate'>,
  startDate: Maybe<string>,
  endDate: Maybe<string>,
): boolean => {
  if (!startDate) {
    return true;
  }
  if (!endDate) {
    return startDate <= offeringEndDate;
  }
  return startDate <= offeringEndDate && endDate >= offeringStartDate;
};

/**
 * Mirror source quantities from the other offerings on the quote. Offerings
 * whose dates overlap the period being saved take precedence, since scheduled
 * changes hold different quantities per period.
 */
const getMirrorSourceQuantitiesOnQuote = (
  config: NonNullable<CustomFieldQuantityConfig>,
  productsById: QuantityAutomationProductsById,
  quote: Maybe<IQuoteRespSchema>,
  {
    excludeQuoteOfferingId,
    startDate,
    endDate,
  }: {
    excludeQuoteOfferingId?: string;
    startDate?: Maybe<string>;
    endDate?: Maybe<string>;
  },
): Record<string, number> => {
  const overlapping: Record<string, number> = Object.create(null);
  const nonOverlapping: Record<string, number> = Object.create(null);
  (quote?.quoteOfferings ?? []).forEach((quoteOffering) => {
    if (quoteOffering.id === excludeQuoteOfferingId) {
      return;
    }
    const bucket = doesPeriodOverlapOffering(quoteOffering, startDate, endDate)
      ? overlapping
      : nonOverlapping;
    Object.entries(
      getMirrorSourceQuantities(config, productsById, quoteOffering.items),
    ).forEach(([matchKey, quantity]) => {
      if (!Object.hasOwn(bucket, matchKey)) {
        bucket[matchKey] = quantity;
      }
    });
  });
  return Object.assign(Object.create(null), nonOverlapping, overlapping);
};

/**
 * Mirror every quantity-locked line in the offering being saved to the
 * quantity of the source line sharing its match key.
 *
 * Sources in this offering are preferred (freshest values). Other offerings
 * are only consulted for lines the cross-offering event cannot have reached:
 * every line of a new offering, and lines newly added to an existing one. For
 * existing lines a stale quote read could clobber a fresher event update, so
 * those rely on the event alone.
 *
 * When this offering holds mirror sources, a callback is returned that, given
 * the saved offering, notifies the other offerings so their locked lines follow.
 */
export const CUSTOMER_AUTOMATION_CustomField_MirrorQuantity = ({
  customerAutomationCustomFieldQuantity: config,
  productsById,
  quoteOffering,
  quote,
  quoteItemsToModify,
  setValue,
}: {
  customerAutomationCustomFieldQuantity: CustomFieldQuantityConfig;
  productsById: QuantityAutomationProductsById;
  quoteOffering?: Maybe<IQuoteOfferingRespSchema>;
  quote?: Maybe<IQuoteRespSchema>;
  quoteItemsToModify: IQuoteOfferingReqSchema;
  /** Omit when the data is not backed by a form (scheduled change creation) */
  setValue?: UseFormSetValue<IQuoteOfferingReqSchema>;
}): ((savedQuoteOffering: IQuoteOfferingRespSchema) => void) | undefined => {
  try {
    if (!config) {
      return;
    }
    const { items, schedule } = quoteItemsToModify;
    // A scheduled change segment carries its own period on `schedule`; the
    // top-level dates are then the copied prior segment's and must be ignored
    // (an absent schedule end date means the period is open-ended).
    const { startDate, endDate } = schedule ?? quoteItemsToModify;
    const sourceQuantityByMatchKey = getMirrorSourceQuantities(
      config,
      productsById,
      items,
    );

    // Lazily scanned: most saves never need the rest of the quote
    let quoteSourceQuantityByMatchKey: Record<string, number> | undefined;
    const getQuoteSourceQuantity = (matchKey: string): number | undefined => {
      if (!quoteSourceQuantityByMatchKey) {
        quoteSourceQuantityByMatchKey = getMirrorSourceQuantitiesOnQuote(
          config,
          productsById,
          quote,
          { excludeQuoteOfferingId: quoteOffering?.id, startDate, endDate },
        );
      }
      return quoteSourceQuantityByMatchKey[matchKey];
    };

    items.forEach((item, index) => {
      const { isQuantityLocked, mirrorMatchKey } =
        getProductQuantityAutomationFlags(config, productsById[item.productId]);
      if (!isQuantityLocked || !mirrorMatchKey) {
        return;
      }
      const isNewLine = !quoteOffering?.id || !item.id;
      const sourceQuantity =
        sourceQuantityByMatchKey[mirrorMatchKey] ??
        (isNewLine ? getQuoteSourceQuantity(mirrorMatchKey) : undefined);
      if (sourceQuantity === undefined || item.quantity === sourceQuantity) {
        return;
      }
      item.quantity = sourceQuantity;
      setValue?.(`items.${index}.quantity`, sourceQuantity);
    });

    if (!Object.keys(sourceQuantityByMatchKey).length) {
      return;
    }

    return (savedQuoteOffering) => {
      dispatchCustomEvent('updateQuoteOfferingMirrorQty', {
        sourceOffering: {
          quoteOfferingId: savedQuoteOffering.id,
          parentQuoteOfferingId: savedQuoteOffering.parentQuoteOfferingId,
          startDate: parseISO(savedQuoteOffering.startDate),
          endDate: parseISO(savedQuoteOffering.endDate),
        },
        quantityByMatchKey: sourceQuantityByMatchKey,
      });
    };
  } catch (ex) {
    logger.error('Error in CUSTOMER_AUTOMATION_CustomField_MirrorQuantity', ex);
  }
};

/**
 * Given the mirror source quantities broadcast by another offering, the
 * quantity-locked lines of this offering whose quantity must change to follow.
 *
 * Returns item ids rather than array indexes: `quoteOffering.items` reflects
 * server order, which can differ from the form's own item order.
 *
 * Match keys with a same-offering source (per `formItems`, the form's current,
 * possibly-unsaved items) are skipped: that source takes precedence on save
 * (see `CUSTOMER_AUTOMATION_CustomField_MirrorQuantity`), so applying the
 * broadcast value here would only be reverted and re-broadcast, oscillating
 * with the other offering.
 */
export const CUSTOMER_AUTOMATION_CustomField_getMirrorEventUpdates = ({
  customerAutomationCustomFieldQuantity: config,
  productsById,
  quoteOffering,
  formItems,
  quantityByMatchKey,
}: {
  customerAutomationCustomFieldQuantity: CustomFieldQuantityConfig;
  productsById: QuantityAutomationProductsById;
  quoteOffering: IQuoteOfferingRespSchema;
  formItems: Pick<IQuoteItemReqSchema, 'productId' | 'quantity'>[];
  quantityByMatchKey: Record<string, number>;
}): { id: string; quantity: number }[] => {
  if (!config) {
    return [];
  }
  const localSourceMatchKeys = new Set(
    Object.keys(getMirrorSourceQuantities(config, productsById, formItems)),
  );
  return quoteOffering.items.flatMap((item) => {
    const { isQuantityLocked, mirrorMatchKey } =
      getProductQuantityAutomationFlags(config, productsById[item.productId]);
    if (
      !isQuantityLocked ||
      !mirrorMatchKey ||
      !Object.hasOwn(quantityByMatchKey, mirrorMatchKey) ||
      !item.id ||
      localSourceMatchKeys.has(mirrorMatchKey)
    ) {
      return [];
    }
    const quantity = quantityByMatchKey[mirrorMatchKey];
    return item.quantity === quantity ? [] : [{ id: item.id, quantity }];
  });
};

/**
 * **************************************************************************
 * Anaplan Automation
 *
 * Saving the "Apps" offering:
 * 1. sets the QUOTE custom field `modules_enabled` (DROPDOWN "1".."5") to the
 *    number of products on that line, clamped to [1, maxModules]. The caller
 *    persists the returned custom-field map once the offering save succeeds.
 * 2. applies the module discount tier matching that count and removes the
 *    other tier discounts; unrelated discounts are kept.
 *
 * The "Core SaaS Platform" custom unit price is a matrix lookup keyed by the
 * quote custom fields `of_employees` (row) and `modules_enabled` (column). It
 * is derived whenever that offering is saved; `useHandleAnaplanCustomFieldChange`
 * re-saves the line when either quote custom field changes.
 * **************************************************************************
 */

type AnaplanConfig = NonNullable<FeatureFlag['customerAutomationAnaplan']>;

/** The tier with the highest threshold at or below `value`, if any */
const findHighestTierAtOrBelow = <Tier>(
  tiers: Tier[],
  getThreshold: (tier: Tier) => number,
  value: number,
): Tier | undefined =>
  tiers.reduce<Tier | undefined>((best, tier) => {
    const threshold = getThreshold(tier);
    if (threshold > value) {
      return best;
    }
    return best === undefined || threshold > getThreshold(best) ? tier : best;
  }, undefined);

/**
 * Parse a count held in a custom field. Accepts numbers and numeric text,
 * including thousands separators and range-style labels ("1,000+", "100-499"),
 * whose leading number places the value in the right matrix row.
 */
export const anaplan_parseCount = (value: unknown): number | undefined => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value !== 'string') {
    return undefined;
  }
  const leadingNumber = value.trim().match(/^(\d[\d,]*)/)?.[1];
  if (!leadingNumber) {
    return undefined;
  }
  const parsed = Number(leadingNumber.replace(/,/g, ''));
  return Number.isFinite(parsed) ? parsed : undefined;
};

/** Number of products on the line, clamped to the dropdown's [1, maxModules] range */
export const anaplan_getModuleCount = (
  items: unknown[],
  maxModules: number,
): number => Math.min(Math.max(1, items.length), Math.max(1, maxModules));

/** Discount of the tier with the highest `minModules` at or below the count */
export const anaplan_getModuleDiscountId = (
  moduleDiscountTiers: AnaplanConfig['moduleDiscountTiers'],
  moduleCount: number,
): string | undefined =>
  findHighestTierAtOrBelow(
    moduleDiscountTiers,
    ({ minModules }) => minModules,
    moduleCount,
  )?.discountId;

/** Replace every module tier discount with the chosen one; other discounts are kept */
export const anaplan_applyModuleDiscount = (
  discountIds: Maybe<string[]>,
  moduleDiscountTiers: AnaplanConfig['moduleDiscountTiers'],
  discountId: string,
): string[] => {
  const tierDiscountIds = new Set(
    moduleDiscountTiers.map(({ discountId: tierDiscountId }) => tierDiscountId),
  );
  return [
    ...(discountIds ?? []).filter((id) => !tierDiscountIds.has(id)),
    discountId,
  ];
};

const haveSameMembers = (left: Maybe<string[]>, right: string[]): boolean => {
  const leftSet = new Set(left ?? []);
  return (
    leftSet.size === new Set(right).size && right.every((id) => leftSet.has(id))
  );
};

/**
 * Matrix price for the Core SaaS line from the quote custom fields, or
 * undefined when either field is missing/unparseable so nothing is written.
 */
export const anaplan_getCoreSaasUnitPrice = (
  config: AnaplanConfig,
  customFields: Maybe<ICustomFieldRecordSchema>,
): number | undefined => {
  if (!customFields) {
    return undefined;
  }
  const employees = anaplan_parseCount(
    customFields[config.employeesCustomFieldKey],
  );
  const modules = anaplan_parseCount(
    customFields[config.modulesCustomFieldKey],
  );
  if (employees === undefined || modules === undefined || modules < 1) {
    return undefined;
  }
  const row = findHighestTierAtOrBelow(
    config.employeeTiers,
    ({ minEmployees }) => minEmployees,
    employees,
  );
  if (!row?.unitPriceByModules.length) {
    return undefined;
  }
  // The last column covers "N or more" modules
  const columnIndex =
    Math.min(Math.floor(modules), row.unitPriceByModules.length) - 1;
  const unitPrice = row.unitPriceByModules[columnIndex];
  return Number.isFinite(unitPrice) ? unitPrice : undefined;
};

/**
 * Apps offering (add or update): apply the module discount for the product
 * count and, when `modules_enabled` must change, return just that field as a
 * patch. The caller merges it into the latest quote custom fields at
 * persistence time (an edit made while the offering saves must not be
 * overwritten by a snapshot taken here). Keyed on
 * `quoteItemsToModify.offeringId`, which is present on create too (no quote
 * offering response exists yet).
 *
 * `quoteCustomFields` should be the freshest values the caller has: a bulk add
 * reuses one submit callback for every offering it creates, so a `quote`
 * snapshot would still hold the pre-bulk value after the Apps line is saved.
 */
export const CUSTOMER_AUTOMATION_Anaplan_Apps = ({
  customerAutomationAnaplan: config,
  quoteCustomFields,
  quoteItemsToModify,
  setValue,
}: {
  customerAutomationAnaplan: FeatureFlag['customerAutomationAnaplan'];
  /** Freshest quote custom fields available (the quote header form's, which update before `quote` does) */
  quoteCustomFields: Maybe<ICustomFieldRecordSchema>;
  quoteItemsToModify: IQuoteOfferingReqSchema;
  setValue: UseFormSetValue<IQuoteOfferingReqSchema>;
}): { customFieldsPatch: ICustomFieldRecordSchema } | undefined => {
  try {
    if (!config || quoteItemsToModify.offeringId !== config.appsOfferingId) {
      return;
    }
    const moduleCount = anaplan_getModuleCount(
      quoteItemsToModify.items,
      config.maxModules,
    );

    const discountId = anaplan_getModuleDiscountId(
      config.moduleDiscountTiers,
      moduleCount,
    );
    if (discountId) {
      const discountIds = anaplan_applyModuleDiscount(
        quoteItemsToModify.discountIds,
        config.moduleDiscountTiers,
        discountId,
      );
      if (!haveSameMembers(quoteItemsToModify.discountIds, discountIds)) {
        quoteItemsToModify.discountIds = discountIds;
        // ensure form (discount picker) updates with changes
        setValue('discountIds', discountIds);
      }
    }

    // The dropdown stores its option value as a string
    const modulesValue = String(moduleCount);
    if (
      String(quoteCustomFields?.[config.modulesCustomFieldKey] ?? '') ===
      modulesValue
    ) {
      return;
    }
    return {
      customFieldsPatch: { [config.modulesCustomFieldKey]: modulesValue },
    };
  } catch (ex) {
    logger.error('Error in CUSTOMER_AUTOMATION_Anaplan_Apps', ex);
  }
};

/**
 * Core SaaS offering (add or update): set the matrix unit price on the
 * configured custom-price product(s). Not gated on an existing `customPrice`
 * so a newly added line is priced too; the request builder only sends
 * `customPrice` for CUSTOM price-model products.
 */
export const CUSTOMER_AUTOMATION_Anaplan_CoreSaas = ({
  customerAutomationAnaplan: config,
  quoteCustomFields,
  quoteItemsToModify,
  setValue,
}: {
  customerAutomationAnaplan: FeatureFlag['customerAutomationAnaplan'];
  /** Freshest quote custom fields available (the quote header form's, which update before `quote` does) */
  quoteCustomFields: Maybe<ICustomFieldRecordSchema>;
  quoteItemsToModify: IQuoteOfferingReqSchema;
  setValue: UseFormSetValue<IQuoteOfferingReqSchema>;
}) => {
  try {
    if (
      !config ||
      quoteItemsToModify.offeringId !== config.coreSaasOfferingId
    ) {
      return;
    }
    const unitAmount = anaplan_getCoreSaasUnitPrice(config, quoteCustomFields);
    if (unitAmount === undefined) {
      return;
    }
    const targetProductIds = new Set(config.coreSaasProductIds);
    quoteItemsToModify.items.forEach((item, index) => {
      if (
        !targetProductIds.has(item.productId) ||
        item.customPrice?.unitAmount === unitAmount
      ) {
        return;
      }
      item.customPrice = { ...item.customPrice, unitAmount };
      setValue(`items.${index}.customPrice.unitAmount`, unitAmount);
    });
  } catch (ex) {
    logger.error('Error in CUSTOMER_AUTOMATION_Anaplan_CoreSaas', ex);
  }
};

/**
 * Does a saved Core SaaS line need re-saving because the quote custom fields
 * now resolve to a different matrix price than the one stored on it?
 */
export const anaplan_coreSaasNeedsResave = ({
  customerAutomationAnaplan: config,
  quoteOffering,
  customFields,
}: {
  customerAutomationAnaplan: FeatureFlag['customerAutomationAnaplan'];
  quoteOffering: Maybe<IQuoteOfferingRespSchema>;
  customFields: Maybe<ICustomFieldRecordSchema>;
}): boolean => {
  if (
    !config ||
    !quoteOffering?.id ||
    quoteOffering.offeringId !== config.coreSaasOfferingId
  ) {
    return false;
  }
  const unitAmount = anaplan_getCoreSaasUnitPrice(config, customFields);
  if (unitAmount === undefined) {
    return false;
  }
  const targetProductIds = new Set(config.coreSaasProductIds);
  return quoteOffering.items.some(
    (item) =>
      targetProductIds.has(item.productId) &&
      (item.customPrice?.amount ?? item.unitPrice) !== unitAmount,
  );
};

/** The matrix-priced Core SaaS product cannot have its unit price edited */
export const CUSTOMER_AUTOMATION_Anaplan_shouldLockCustomPrice = ({
  customerAutomationAnaplan: config,
  offeringId,
  productId,
}: {
  customerAutomationAnaplan: FeatureFlag['customerAutomationAnaplan'];
  offeringId: string;
  productId: string;
}): boolean =>
  !!config &&
  offeringId === config.coreSaasOfferingId &&
  config.coreSaasProductIds.includes(productId);
